import { createApp, h, App } from 'vue';
import ToastComponent from '@/components/toast/index.vue';

interface ToastInstance {
  id: string;
  app: App;
  container: HTMLElement;
  verticalPosition: number;
  visible: boolean;
}

const toastList: ToastInstance[] = [];
const verticalGap = 10; // Toast 之间的垂直间隔
const initialOffset = 30; // 初始顶部偏移

export function Toast(
  message: string,
  variant?: 'success' | 'error' | 'warning' | 'info',
  duration: number | null = 3000,
): string {
  // Check if a toast with the same message already exists
  const existingToast = toastList.find(toast => {
    const toastMessage = toast.container.querySelector('.toast-message')?.textContent;
    return toastMessage === message;
  });

  // If duplicate exists, return its ID instead of creating a new one
  if (existingToast) {
    console.debug('[toast] Duplicate message detected, reusing existing toast:', existingToast.id);
    return existingToast.id;
  }

  // Ensure duration is always a valid positive number or null for persistent
  const validDuration = duration === null ? null : typeof duration === 'number' && duration > 0 ? duration : 3000;
  const id = `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // 计算新 Toast 的垂直位置（从顶部开始）
  const verticalPosition = initialOffset;

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = `${verticalPosition}px`;
  container.style.left = '50%';
  container.style.transform = 'translateX(-50%)';
  container.style.zIndex = '9999';
  container.style.pointerEvents = 'none'; // Don't block interactions
  container.style.transition = 'top 0.3s ease';

  const app = createApp({
    render() {
      return h(ToastComponent, {
        message,
        duration: validDuration,
        variant,
      });
    },
  });

  const instance: ToastInstance = {
    id,
    app,
    container,
    verticalPosition,
    visible: true,
  };

  // 将 Toast 添加到 DOM
  document.body.appendChild(container);
  app.mount(container);
  const addedHeight = container.offsetHeight + verticalGap;
  if (toastList.length > 0) {
    toastList.forEach(existingToast => {
      existingToast.verticalPosition += addedHeight;
      existingToast.container.style.top = `${existingToast.verticalPosition}px`;
    });
  }
  toastList.unshift(instance);

  // 设置定时器，自动移除 Toast (only if duration is not null)
  if (validDuration !== null) {
    setTimeout(() => {
      closeToast(id);
    }, validDuration);
  }

  return id;
}

/**
 * 关闭指定的Toast实例，如果未指定id则关闭所有Toast
 * @param id Toast实例的id，如果不传则关闭所有Toast
 */
export function closeToast(id?: string): void {
  if (id) {
    // 关闭指定的Toast
    const index = toastList.findIndex(item => item.id === id);
    if (index !== -1) {
      const toast = toastList[index];
      toast.visible = false;

      const removedHeight = toast.container.offsetHeight + verticalGap;

      // 立即更新后续 Toast 的位置
      for (let i = index + 1; i < toastList.length; i++) {
        const nextToast = toastList[i];
        nextToast.verticalPosition -= removedHeight;
        nextToast.container.style.top = `${nextToast.verticalPosition}px`;
      }

      // 从列表中移除
      toastList.splice(index, 1);

      // 等待过渡动画完成后移除 DOM
      setTimeout(() => {
        if (toast.container.parentNode === document.body) {
          document.body.removeChild(toast.container);
          toast.app.unmount();
        }
      }, 500);
    }
  } else {
    // 关闭所有Toast
    const toastsCopy = [...toastList];
    toastList.length = 0; // 清空数组

    toastsCopy.forEach(toast => {
      toast.visible = false;
      if (toast.container.parentNode === document.body) {
        document.body.removeChild(toast.container);
        toast.app.unmount();
      }
    });
  }
}
